import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from "@simplewebauthn/browser";
import CustomModal from "../ui/modal/modal.component";
import { RegisterPasskeyForm } from "../../workspaces/profile/managePasskeys/registerPasskeyForm.component";
import { useAuthContext } from "../../contexts/auth";
import { useWebAuthnCredentials } from "../../api/webauthn";

const SETUP_BIOMETRIC_LOGIN_DISMISSED_KEY = "setupBiometricLoginModalDismissed";

export const SetupBiometricLoginModal = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const { list } = useWebAuthnCredentials();
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    // Wait until the profile-completion prompt is out of the way so the two
    // modals never compete for attention on the same login.
    if (!currentUser || !currentUser.first_name || !currentUser.last_name) return;
    if (sessionStorage.getItem(SETUP_BIOMETRIC_LOGIN_DISMISSED_KEY) === "true") return;
    if (!browserSupportsWebAuthn()) return;

    let cancelled = false;
    (async () => {
      const hasPlatformAuthenticator = await platformAuthenticatorIsAvailable();
      if (!hasPlatformAuthenticator || cancelled) return;

      const credentials = await list();
      if (credentials.length === 0 && !cancelled) setOpened(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleDismiss = (value: boolean) => {
    if (!value) {
      sessionStorage.setItem(SETUP_BIOMETRIC_LOGIN_DISMISSED_KEY, "true");
    }
    setOpened(value);
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t("Set up biometric login (Face ID, Touch ID, Windows Hello) for faster, more secure sign-in next time. You can also do this later from your profile settings.")}
      </Typography>
      <RegisterPasskeyForm onSuccess={() => handleDismiss(false)} />
      <Button onClick={() => handleDismiss(false)} fullWidth>
        {t("Maybe later")}
      </Button>
    </Box>
  );

  return (
    <CustomModal
      opened={opened}
      setOpened={handleDismiss}
      title={t("Set up biometric login")}
      content={content}
      width={500}
    />
  );
};
